import { Component, OnInit, Input, EventEmitter, Output, OnDestroy, Inject } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MyDayOfWeek, OptimizeRoute } from '@app/@core/enums';
import { VehicleService } from '@app/@core/services/vehicle.service';
import { DriverService } from '@app/@core/services/driver.service';
import moment from 'moment';
import { RouteService } from '@app/@core/services/route.service';
import { NbToastrService } from '@nebular/theme';
import { CreateRouteModel, getRouteStartDate } from '@app/pages/routes/models/createRouteModel';
import { GetWeekDayMomentNumber } from '@app/@core/utils/enum-helpers';
import { NbDateService } from '@nebular/theme';
import { LaddaService } from '@app/@core/services/ladda.service';
import { Subscription } from 'rxjs';
import { Location } from '@core/models/location';
import { DropDownModel } from '@app/pages/customer/models/dropdown.model';
import { RouteModel } from '@app/shared/models/route-allocation.model';
import { AppConstant } from '@app/@core/constants/app-constant';
import { DATE_SERVICE_INTERFACE, IDateService } from '@app/@core/services/interfaces/i-date-service.inteface';
import { TitleStrategy } from '@angular/router';
@Component({
  selector: 'route-form-dialog',
  templateUrl: './route-form-dialog.component.html',
  styleUrls: ['./route-form-dialog.component.scss'],
  standalone: false
})
export class RouteFormDialogComponent implements OnInit, OnDestroy {
  @Input() title: string;
  @Input() event: any;
  @Input() formData: RouteModel;
  @Input() currentDriver: DropDownModel;
  @Input() currentVehicle: DropDownModel;

  @Input() selectedDate: moment.Moment = moment();
  @Output() isClosed = new EventEmitter<boolean>(false);
  @Output() isSaved = new EventEmitter<any>(false);
  vehicleDropdownData = [];
  driverDropdownData = [];
  isSelectedLocation = false;

  currentStartPointAddress = '';
  isEditMode = false;
  
  minDate: Date;
  isLoading = false;
  subscriptions: Array<Subscription> = [];
  dateFormat: string;

  get selectedDateString() {
    return this.selectedDate?.format(this.dateFormat);
  }

  OptimizeOption = [
    { value: OptimizeRoute.TimeEfficient, display: 'Time efficient' },
    { value: OptimizeRoute.Distance, display: 'Distance' },
  ];

  ApplyOnList = [
    { display: 'This date', value: true },
    { display: 'This date moving forward', value: false },
  ];

  dayOfWeekDropDown = [
    { value: MyDayOfWeek.Monday, display: 'Mon' },
    { value: MyDayOfWeek.Tuesday, display: 'Tue' },
    { value: MyDayOfWeek.Wednesday, display: 'Wed' },
    { value: MyDayOfWeek.Thursday, display: 'Thu' },
    { value: MyDayOfWeek.Friday, display: 'Fri' },
    { value: MyDayOfWeek.Saturday, display: 'Sat' },
    { value: MyDayOfWeek.Sunday, display: 'Sun' },
  ];

  mainForm = new FormGroup({
    id: new FormControl(0),
    name: new FormControl('', Validators.compose([Validators.required, Validators.maxLength(255)])),
    driver: new FormControl<DropDownModel>(null, Validators.required),
    vehicle: new FormControl<DropDownModel>(null, Validators.required),
    routeDay: new FormControl(MyDayOfWeek.Monday, Validators.required),
    startDate: new FormControl<any>(null, Validators.required),
    endDate: new FormControl(),
    startPoint: new FormControl<any>(null),
    optimizeRoute: new FormControl(OptimizeRoute.TimeEfficient),
    applyOn: new FormControl(null, Validators.required)
  });

  constructor(
    private vehicleService: VehicleService,
    private driverService: DriverService,
    private routeService: RouteService,
    private toastrService: NbToastrService,
    protected nbDateService: NbDateService<Date>,
    protected laddaService: LaddaService,
    @Inject(DATE_SERVICE_INTERFACE) private dateService: IDateService
  ) {
    this.subscriptions.push(
      this.dateService.getDateFormat().subscribe(value => {
        this.dateFormat = value;
      })
    );
  }

  ngOnInit() {
    // Add new route from route allocation page
    if (this.currentDriver) {
      this.driverDropdownData.push(this.currentDriver);
      this.mainForm.patchValue({
        driver: this.currentDriver,
        routeDay: this.selectedDate.weekday(),
        startDate: getRouteStartDate(this.selectedDate),
      });
    }
    else if (this.currentVehicle) {
      this.vehicleDropdownData.push(this.currentVehicle);
      this.mainForm.patchValue({
        vehicle: this.currentVehicle,
        routeDay: this.selectedDate.weekday(),
        startDate: getRouteStartDate(this.selectedDate),
      });
    } else {
      if (this.selectedDate) {
        this.mainForm.patchValue({
          routeDay: this.selectedDate.weekday(),
          startDate: getRouteStartDate(this.selectedDate),
        });
      }
    }

    // for Edit mode
    this.isEditMode = this.formData != null;
    if (this.isEditMode) {
      this.mainForm.controls.startDate.disable();
      this.currentVehicle = {
        value: this.formData.vehicleId,
        display: this.formData.vehicle,
      };
      this.currentDriver = {
        value: this.formData.driverId,
        display: this.formData.driver,
      };
      this.vehicleDropdownData.push(this.currentVehicle);
      this.driverDropdownData.push(this.currentDriver);

      if (this.formData.startPointAddress1) {
        this.currentStartPointAddress = `${this.formData.startPointAddress1}`
          + `, ${this.formData.startPointCity ? this.formData.startPointCity : this.formData.startPointState}`
          + `, ${this.formData.startPointCountry}`;
      }

      this.mainForm.patchValue({
        id: this.formData.routeId,
        name: this.formData.routeName,
        routeDay: this.formData.routeDay,
        vehicle: this.currentVehicle,
        driver: this.currentDriver,
        startDate: moment(this.formData.routeStartDate, 'YYYY-MM-DD'),
        endDate: this.formData.routeEndDate ? moment(this.formData.routeEndDate, 'YYYY-MM-DD') : null,
        startPoint: {
          address1: this.formData.startPointAddress1,
          address2: this.formData.startPointAddress2,
          city: this.formData.startPointCity,
          country: this.formData.startPointCountry,
          state: this.formData.startPointState,
          zipCode: this.formData.startPointZipCode,
          latitude: this.formData.startPointLatitude,
          longitude: this.formData.startPointLongitude,
        },
        optimizeRoute: this.formData.optimizeRoute,
      });
    } else {
      this.minDate = this.nbDateService.today();
      this.mainForm.patchValue({ applyOn: true });
    }

    this.subscriptions.push(this.laddaService.laddaState.subscribe(value => {
      this.isLoading = value.show;
    }));
  }

  isGreaterThanStartDate() {
    if (!(this.mainForm.getRawValue().startDate == null
      || this.mainForm.getRawValue().startDate === ''
      || this.mainForm.getRawValue().startDate === undefined
      || this.mainForm.value.endDate == null
      || this.mainForm.value.endDate === ''
      || this.mainForm.value.endDate === undefined)) {

      const valid = moment(this.mainForm.value.endDate).isSameOrAfter(this.mainForm.getRawValue().startDate, 'day');
      if (!valid) {
        this.mainForm.controls.endDate.setErrors({
          cannotBeBeforeStartDate: 'End Date can not be before Start Date',
        });
      } else {
        this.mainForm.controls.endDate.setErrors(null);
      }
    }
  }

  cancel() {
    this.isClosed.emit(true);
  }

  save() {
    this.isLoading = true;
    // avoiding the last errors being cached, it will clear all and re-validate again.
    this.clearFormControlErrors();
    this.mainForm.markAllAsTouched();
    this.mainForm.markAsDirty()
    if (this.mainForm.value.endDate != null) {
      let firstRouteDate = moment(this.mainForm.getRawValue().startDate);
      const routeWeekDate = GetWeekDayMomentNumber(this.mainForm.value.routeDay);
      while (firstRouteDate.isSameOrBefore(this.mainForm.value.endDate, 'day')) {
        const firstRouteWeekDay = firstRouteDate.weekday();
        if (firstRouteWeekDay === routeWeekDate) {
          break;
        }
        firstRouteDate = firstRouteDate.add(1, 'days');
      }

      if (firstRouteDate.isAfter(this.mainForm.value.endDate, 'day')) {
        this.mainForm.controls.routeDay.setErrors({ objectNotInRange: 'Route day is not available from Start to End time of route' });
      }
    }

    if (this.mainForm.valid) {
      const data: CreateRouteModel = {
        id: this.mainForm.value.id,
        name: this.mainForm.value.name,
        vehicleId: this.mainForm.value.vehicle.value,
        userTenantId: this.mainForm.value.driver.value,
        routeDay: this.mainForm.value.routeDay,
        startDate: this.mainForm.getRawValue().startDate,
        endDate: this.mainForm.value.endDate,
        startPointAddress1: this.mainForm.value.startPoint?.address1,
        startPointAddress2: this.mainForm.value.startPoint?.address2,
        startPointCity: this.mainForm.value.startPoint?.city,
        startPointCountry: this.mainForm.value.startPoint?.country,
        startPointState: this.mainForm.value.startPoint?.state,
        startPointZipCode: this.mainForm.value.startPoint?.zipCode,
        startPointLatitude: this.mainForm.value.startPoint?.latitude,
        startPointLongitude: this.mainForm.value.startPoint?.longitude,
        optimizeRoute: this.mainForm.value.optimizeRoute,
        selectedDate: this.selectedDate,
        isApplOnThisWeek: Boolean(this.mainForm.value.applyOn)
      };

      if (this.isEditMode) {
        // Edit
        SelectedDate: moment(this.selectedDate, 'YYYY-MM-DD'),

          this.routeService.update(data.id.toString(), data, false).subscribe(res => {
            this.isSaved.emit({ vehicleId: this.mainForm.value.vehicle.value, routeId: this.mainForm.value.id });
            this.toastrService.success('', `Route has been successfully updated.`);
          }, err => {
            this.errorHandler(err);
          });

      } else {
        // Create
        this.routeService.create(data, false).subscribe(res => {
          this.isSaved.emit({ vehicleId: this.mainForm.value.vehicle.value });
          this.toastrService.success('', `Route has been successfully created.`);
        }, err => {
          this.errorHandler(err);
        });
      }
    }
    else {
      this.isLoading = false;
    }
  }

  errorHandler(err) {
    switch (err.error.message) {
      case 'CustomerAllocatedAndDownEndDate':
        err.error.message = `Cannot change end date, as customers exist on this route.
                    All customers must be removed from route before route end date can be changed.`;
        this.mainForm.controls.endDate.setErrors({ error: err.error.message });
        this.mainForm.controls.endDate.markAsTouched();
        break;
      case 'VehicleSelectedInDayRange':
        err.error.message = 'The vehicle has already been selected in other route on this week day.';
        this.mainForm.controls.vehicle.setErrors({ error: err.error.message });
        this.mainForm.controls.vehicle.markAsTouched();
        break;
      case 'DriverSelectedInDayRange':
        err.error.message = 'The driver has already been selected in other route on this week day.';
        this.mainForm.controls.driver.setErrors({ error: err.error.message });
        this.mainForm.controls.driver.markAsTouched();
        break;
      case 'ChangedRouteDayWhileWorking':
        err.error.message = `Cannot change Route day, as customers exist on this route.
                      All customers must be removed from route before route day can be changed.`;
        this.mainForm.controls.routeDay.setErrors({ error: err.error.message });
        this.mainForm.controls.routeDay.markAsTouched();
        break;
      default:
        this.toastrService.danger('', err.error.message);
        break;
    }
  }

  onLocationSelected(event) {
    const gMapLocation = Location.getGmapAddress(event);
    this.mainForm.patchValue({
      startPoint: {
        address1: gMapLocation.address,
        city: gMapLocation.city,
        state: gMapLocation.state,
        zipCode: gMapLocation.zipCode,
        country: gMapLocation.country,
        longitude: gMapLocation.longitude,
        latitude: gMapLocation.latitude,
      },
    });
    this.isSelectedLocation = false;
  }

  onLocationSearchChange(event) {
    this.mainForm.patchValue({
      startPoint: {
        address1: event,
      }
    });
    this.isSelectedLocation = true;
  }

  onDriverFilterChange(event) {
    this.driverService.getDropdown(event).subscribe((res: Array<any>) => {
      this.driverDropdownData = res;
    });
  }

  onVehicleFilterChange(event) {
    this.vehicleService.getDropdown(event).subscribe((res: Array<any>) => {
      this.vehicleDropdownData = res;
    });
  }

  onAutoCompleteFocusOut(control: any) {
    if (control.value != null && control.value !== undefined) {
      if (control.value.value == null || control.value.value === undefined) {
        control.patchValue(null);
      }
    }
  }

  clearFormControlErrors() {
    // In case save with empty form, the errors didnt show, so I comment 2 below lines
    if (this.mainForm.controls.driver.errors && !this.mainForm.controls.driver.errors['required']) {
      this.mainForm.controls.driver.setErrors(null);
    }

    if (this.mainForm.controls.vehicle.errors && !this.mainForm.controls.vehicle.errors['required']) {
      this.mainForm.controls.vehicle.setErrors(null);
    }

    this.mainForm.controls.routeDay.setErrors(null);
    // DE-244
    // this.mainForm.controls.endDate.setErrors(null);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

}
